<template>
  <!-- Show Invoice Details in a Popup Modal -->
  <div v-if="showModal" ref="modalContent" class="modal-overlay">
    <div class="modal-content" @click.stop>

      <!-- Top-right Close icon button -->
      <button class="modal-close-btn" @click="closeModal" title="Close">
        <i class="fa fa-times"></i>
      </button>


      <!-- Top-right Close icon button -->
      <button class="modal-close-btn" @click="closeModal" title="Close">
        <i class="fa fa-times"></i>
      </button>

      <!-- Show Cancelled Badge if invoice.is_cancel === '1' -->
      <img
          v-if="!invoiceLoading && invoice && invoice.is_cancel === '1'"
          src="../../assets/badges/invoice_cancelled_badge.png"
          alt="Cancelled Badge"
          class="cancelled-badge"
      />

      <!-- Loading spinner while fetching invoice data -->
      <div v-if="invoiceLoading" class="invoice-loading-overlay">
        <div class="invoice-loading-box">
          <span class="spinner-border spinner-border-lg" role="status" aria-hidden="true"></span>
          <p class="mt-3 mb-0">Loading invoice...</p>
        </div>
      </div>

      <!-- ===== INVOICE DOCUMENT AREA ===== -->
      <div v-if="!invoiceLoading && invoice" class="invoice-document">

        <!-- Invoice Title -->
        <div class="invoice-title-box">
          <h2>{{ invoiceTitle }}</h2>
        </div>

        <!-- Row 1: Date of Invoice | Tax Invoice No -->
        <div class="invoice-row-grid">
          <div class="inv-cell">
            <span class="inv-label">Date of Invoice:</span>
            <span class="inv-value">{{ formatDate(invoice.created_at) }}</span>
          </div>
          <div class="inv-cell">
            <span class="inv-label">
              <template v-if="isVatInvoice">Tax Invoice No. :</template>
              <template v-else>Invoice Number :</template>
            </span>
            <span class="inv-value">{{ invoice.invoice_no }}</span>
          </div>
        </div>

        <!-- Row 2: Supplier Info | Purchaser Info -->
        <div class="invoice-row-grid">
          <!-- Left: Supplier -->
          <div class="inv-cell inv-cell-tall">
            <div v-if="invoice.distributor_vat_liable == 1" class="inv-field">
              <span class="inv-label-col">Supplier's TIN :</span>
              <span class="inv-value">{{ invoice.distributor_vat_no || 'N/A' }}</span>
            </div>
            <div class="inv-field">
              <span class="inv-label-col">Supplier's Name :</span>
              <span class="inv-value">{{ invoice.distributor_name }}</span>
            </div>
            <div class="inv-field">
              <span class="inv-label-col">Address :</span>
              <span class="inv-value">{{ invoice.distributor_address }}</span>
            </div>
            <div class="inv-field inv-field-bottom">
              <span class="inv-label-col">Telephone No. :</span>
              <span class="inv-value">{{ invoice.distributor_mobile }}</span>
            </div>
          </div>

          <!-- Right: Purchaser -->
          <div class="inv-cell inv-cell-tall">
            <div v-if="isVatInvoice" class="inv-field">
              <span class="inv-label-col">Purchaser's TIN :</span>
              <span class="inv-value">
                {{ invoice.is_customer_invoice ? (invoice.customer_vat_no || 'N/A') : (invoice.dealer_vat_no || 'N/A') }}
              </span>
            </div>
            <div class="inv-field">
              <span class="inv-label-col">Purchaser's Name :</span>
              <span class="inv-value">{{ purchaserName }}</span>
            </div>
            <div class="inv-field">
              <span class="inv-label-col">Address :</span>
              <span class="inv-value">{{ purchaserAddr }}</span>
            </div>
            <div class="inv-field inv-field-bottom">
              <span class="inv-label-col">Telephone No. :</span>
              <span class="inv-value">{{ purchaserTel }}</span>
            </div>
          </div>
        </div>

        <!-- Row 3: Date of Supply | Place of Supply (VAT only) -->
        <div v-if="isVatInvoice" class="invoice-row-grid">
          <div class="inv-cell">
            <span class="inv-label">Date of Supply :</span>
            <span class="inv-value">{{ formatDate(invoice.created_at) }}</span>
          </div>
          <div class="inv-cell">
            <span class="inv-label">Place of Supply :</span>
            <span class="inv-value">{{ invoice.distributor_address }}</span>
          </div>
        </div>

        <!-- Row 4: Additional Information (VAT only) -->
        <div v-if="isVatInvoice" class="invoice-row-single">
          <div class="inv-cell" style="flex-direction: column; align-items: flex-start; gap: 4px;">
            <div class="inv-label">Additional Information if any:</div>
            <template v-if="!invoice.is_customer_invoice">
              <div class="inv-value" style="margin-left: 0;">Name: {{ invoice.dealer_name || '' }}</div>
              <div class="inv-value" style="margin-left: 0;">Address: {{ invoice.dealer_address || '' }}</div>
              <div class="inv-value" style="margin-left: 0;">Telephone: {{ invoice.dealer_mobile || '' }}</div>
            </template>
          </div>
        </div>

        <!-- Items Table -->
        <table class="invoice-table">
          <thead>
            <tr>
              <th class="col-ref text-left">Reference</th>
              <th class="col-desc text-left">Description of Goods or Services</th>
              <th class="col-qty">Quantity</th>
              <th class="col-unit text-right">Unit Price</th>
              <th class="col-amt text-right">Amount Excluding VAT (Rs.)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in invoice.items" :key="item.item_name">
              <td class="text-left">{{ item.item_sap_code }}</td>
              <td class="text-left">{{ item.item_name }} <span class="item-type">({{ item.invoice_type }})</span></td>
              <td class="text-center">{{ parseInt(item.item_qty) }}</td>
              <td class="text-right">{{ formatNumber(item.item_price) }}</td>
              <td class="text-right">{{ formatNumber(item.item_net_total) }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="summary-tr">
              <td colspan="4">Total Value of Supply:</td>
              <td class="text-right">Rs. {{ invoice.net_total }}</td>
            </tr>
            <tr v-if="isVatInvoice" class="summary-tr">
              <td colspan="4">VAT Amount (Total Value of Supply @ {{ invoice.vat_percentage }}%)</td>
              <td class="text-right">Rs. {{ invoice.vat }}</td>
            </tr>
            <tr class="summary-tr">
              <td colspan="4">Total Amount/consideration including VAT:</td>
              <td class="text-right">Rs. {{ invoice.gross_total }}</td>
            </tr>
            <!-- Total Paid Amount -->
            <tr class="summary-tr">
              <td colspan="4">Total Paid Amount:</td>
              <td class="text-right">Rs. {{ invoice.total_payment }}</td>
            </tr>
            <!-- Balance Amount -->
            <tr class="summary-tr">
              <td colspan="4">Balance Amount:</td>
              <td class="text-right">Rs. {{ invoice.balance_payment }}</td>
            </tr>
          </tfoot>
        </table>

        <!-- Total Amount in Words -->
        <div class="invoice-row-single mt-3">
          <div class="inv-cell">
            <span class="inv-label">Total Amount in words:&nbsp;</span>
            <span class="inv-value">{{ numberToWords(parseFloat(String(invoice.gross_total).replace(/,/g, ''))) }}</span>
          </div>
        </div>

        <!-- Mode of Payment -->
        <div class="invoice-row-single">
          <div class="inv-cell">
            <span class="inv-label">Mode of Payment:&nbsp;</span>
            <span class="inv-value">{{ paymentMethods }}</span>
          </div>
        </div>

      </div><!-- end invoice-document -->

      <!-- Show Invoice Payments -->
      <div v-if="!invoiceLoading && invoice && showHistory && invoice.payments && invoice.payments.length > 0" class="invoice-history">
        <h3>Payment History</h3>
        <table class="invoice-history-table invoice-table-fullwidth">
          <thead>
          <tr>
            <th class="text-left">Payment Method</th>
            <th class="text-right">Amount (Rs)</th>
            <th class="text-center">Timestamp</th>
            <th v-if="hasCancellationDetails">Cancellation Details</th>
            <th>Status</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="history in invoice.payments" :key="history.created_at">
            <td>
              <div>{{ history.method }}</div>
              
              <!-- Cheque -->
              <div v-if="history.method === 'Cheque'" class="payment-detail">
                No: {{ history.cheque_no || '—' }}<span v-if="history.cheque_date && history.cheque_date !== '1990-01-01'">, Date: {{ formatDate(history.cheque_date) }}</span>
              </div>

              <!-- Bank Transfer -->
              <div v-else-if="history.method === 'Bank Transfer'" class="payment-detail">
                Acc/No: {{ maskBankAccount(history.bank_account_details?.account_no) }}, Bank: {{ history.bank_account_details?.bank_name || '—' }}, Branch: {{ history.bank_account_details?.branch_name || '—' }}
              </div>

              <!-- Advance -->
              <div v-else-if="history.method === 'Advance'" class="payment-detail">
                Original Method: {{ history.advance_payment?.pay_method || '—' }}
                <span v-if="history.advance_payment?.pay_method === 'Cheque'">
                  (No: {{ history.advance_payment?.cheque_no || '—' }}<span v-if="history.advance_payment?.cheque_date && history.advance_payment?.cheque_date !== '1990-01-01'">, Date: {{ formatDate(history.advance_payment?.cheque_date) }}</span>)
                </span>
                <span v-else-if="history.advance_payment?.pay_method === 'Bank Transfer' || history.advance_payment?.bank_reference">
                  (Ref: {{ history.advance_payment?.bank_reference || '—' }})
                </span>
                <span v-else-if="history.advance_payment?.pay_method === 'Credit/Debit Card'">
                  (Card Type: {{ history.advance_payment?.card_type || '—' }}, Last 4 digits: {{ history.advance_payment?.card_last4_digits || '—' }})
                </span>
                <span v-else-if="history.advance_payment?.pay_method === 'Mobile Payment'">
                  (Type: {{ history.advance_payment?.mobile_payment_type || '—' }}, Provider: {{ history.advance_payment?.mobile_provider || '—' }})
                </span>
                <span v-else-if="history.advance_payment?.pay_method === 'Online Payment'">
                  (Gateway: {{ history.advance_payment?.online_gateway || '—' }})
                </span>
              </div>

              <!-- Credit/Debit Card -->
              <div v-else-if="history.method === 'Credit/Debit Card'" class="payment-detail">
                Card Type: {{ history.card_type || '—' }}, Last 4 digits: {{ history.card_last4_digits || '—' }}
              </div>

              <!-- Mobile Payment -->
              <div v-else-if="history.method === 'Mobile Payment'" class="payment-detail">
                Mobile Payment Type: {{ history.mobile_payment_type || '—' }}, Mobile Provider: {{ history.mobile_provider || '—' }}
              </div>

              <!-- Online Payment -->
              <div v-else-if="history.method === 'Online Payment'" class="payment-detail">
                Online Payment Gateway: {{ history.online_gateway || '—' }}
              </div>
            </td>
            <td class="text-right">{{ formatNumber(history.amount) }}</td>
            <td class="text-center">{{ formatTimeStamp(history.paid_at) }}</td>

            <td v-if="hasCancellationDetails">
              <div v-if="history.canceled_by">
                <dl class="cancellation-details">
                  <dd>Cancelled By: {{ history.canceled_by || '—' }}</dd>
                  <dd>Cancelled At: {{ history.canceled_at || '—' }}</dd>
                  <dd>Remark: {{ history.cancel_remark || '—' }}</dd>
                </dl>
              </div>
            </td>

            <td>
              <span :class="{
                'status-label status-pending': history.status === '1',
                'status-label status-completed': history.status === '2',
                'status-label status-cancelled': history.status === '3',
                'status-label status-redeemed': history.status === '4'
              }">
                {{ ShowStatus(history.status) }}
              </span>
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <!-- Action Buttons (bottom) — Close removed; only Cancel & Reprint remain -->
      <div v-if="!invoiceLoading && invoice" class="invoice-actions mt-4">
        <!-- Cancel button — only if active -->
        <button v-if="invoice.is_cancel === '0'" @click="handleCancelClick" class="cancel-button">Cancel</button>

        <!-- Reprint button — only if active -->
        <button v-if="invoice.is_cancel === '0'" @click="printInvoice" class="btn button-default">
          <i class="fa fa-print" aria-hidden="true"></i> Reprint
        </button>
      </div>
    </div>
  </div>

  <!-- Bottom Sheet for Cancel Invoice -->
  <div v-if="showBottomSheet" class="bottom-sheet-overlay" @click="showBottomSheet = false">
    <div class="bottom-sheet" @click.stop>
      <h3>Cancel Invoice</h3>
      <label>Remark:</label>
      <textarea
          v-model="cancelRemark"
          class="bottom-sheet-textarea"
          :class="{ 'border-red': remarkError }"
          placeholder="Add your remark here..."
          maxlength="200"
          @input="validateRemark"
      ></textarea>

      <!-- Error Message -->
      <p v-if="remarkError" class="error-message">Please add a remark.</p>

      <!-- Character Counter -->
      <div class="character-counter">
        {{ cancelRemark.length }}/200
      </div>

      <div class="bottom-sheet-actions">
        <button @click="closeBottomSheet" class="btn-exit">Exit</button>
        <button @click="confirmCancel" class="btn-cancel" :disabled="loading">
          <span v-if="loading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          <span v-else>Cancel Invoice</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { cancelInvoice, fetchInvoiceDetails } from "@/services/dataService";
import { useToast } from 'vue-toastification';
import { ToWords } from 'to-words';

const toWords = new ToWords({
  localeCode: 'en-US',
  converterOptions: {
    currency: true,
    ignoreDecimal: false,
    ignoreZeroCurrency: false,
    currencyOptions: {
      name: 'Rupee',
      plural: 'Rupees',
      symbol: 'Rs.',
      fractionalUnit: {
        name: 'Cent',
        plural: 'Cents',
        symbol: '',
      },
    },
  },
});

export default {
  props: {
    showModal: {
      type: Boolean,
      required: true
    },
    invoiceNo: {
      type: String,
      default: null
    }
  },

  data() {
    return {
      token: localStorage.getItem('token_set') || '',
      profileId: localStorage.getItem('profile_id'),
      invoice: null,
      invoiceLoading: false,
      showHistory: true,
      showBottomSheet: false,
      cancelRemark: '',
      remarkError: false,
      loading: false
    };
  },

  watch: {
    showModal(newVal) {
      if (newVal && this.invoiceNo) {
        this.loadInvoiceDetails();
      } else if (!newVal) {
        // Reset invoice data when modal closes
        this.invoice = null;
      }
    }
  },

  computed: {
    purchaserName() {
      if (!this.invoice) return 'N/A';
      if (this.invoice.is_customer_invoice) {
        return this.invoice.customer_name || 'N/A';
      }
      if (this.isVatInvoice) {
        return this.invoice.original_name || 'N/A';
      }
      return this.invoice.dealer_name || 'N/A';
    },

    purchaserAddr() {
      if (!this.invoice) return 'N/A';
      if (this.invoice.is_customer_invoice) {
        return this.invoice.customer_address || 'N/A';
      }
      if (this.isVatInvoice) {
        return this.invoice.original_address || 'N/A';
      }
      return this.invoice.dealer_address || 'N/A';
    },

    purchaserTel() {
      if (!this.invoice) return 'N/A';
      if (this.invoice.is_customer_invoice) {
        return this.invoice.customer_mobile || 'N/A';
      }
      if (this.isVatInvoice) {
        return this.invoice.original_telephone || 'N/A';
      }
      return this.invoice.dealer_mobile || 'N/A';
    },

    hasCancellationDetails() {
      return this.invoice?.payments?.some(p => {
        return p.canceled_by && String(p.canceled_by).trim() !== '' && String(p.canceled_by).toLowerCase() !== 'null';
      }) ?? false;
    },
    
    isVatInvoice() {
      if (!this.invoice) return false;
      if (this.invoice.is_customer_invoice) {
        return this.invoice.customer_vat_liable == 1;
      }
      return this.invoice.dealer_vat_liable == 1;
    },
    
    invoiceTitle() {
      return this.isVatInvoice ? 'TAX INVOICE' : 'INVOICE';
    },

    paymentMethods() {
      if (!this.invoice) return '—';
      const methods = [...new Set(
        (this.invoice.payments || [])
          .filter(p => p.status != 3)
          .map(p => p.method)
          .filter(Boolean)
      )];
      
      const balance = parseFloat(String(this.invoice.balance_payment).replace(/,/g, '')) || 0;
      if (balance > 0) {
        methods.push('Credit');
      }

      return methods.length ? methods.join(' / ') : '—';
    }
  },

  //Show messages in the toaster
  setup() {
    const toast = useToast(); // Initialize the toaster
    return { toast };
  },

  //Functions
  methods: {
    // Fetch invoice details from API
    async loadInvoiceDetails() {
      if (!this.invoiceNo) return;
      this.invoiceLoading = true;
      this.invoice = null;
      try {
        const response = await fetchInvoiceDetails(this.invoiceNo, this.token);
        this.invoice = response.data;
      } catch (error) {
        console.error('Error fetching invoice details:', error);
        useToast().error('Failed to load invoice details.');
      } finally {
        this.invoiceLoading = false;
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return '—';
      const datePart = dateStr.split(' ')[0];
      const [year, month, day] = datePart.split('-');
      if (!year || !month || !day) return dateStr;
      return `${month}/${day}/${year}`;
    },

    formatTimeStamp(timeStamp) {
      if (!timeStamp) return '—';

      const [datePart, timePart] = timeStamp.split(' ');
      if (!datePart || !timePart) return timeStamp;

      const [year, month, day] = datePart.split('-');
      let [hour, minute] = timePart.split(':');

      hour = parseInt(hour, 10);

      const ampm = hour >= 12 ? 'PM' : 'AM';
      hour = hour % 12 || 12; // Convert 0 to 12

      return `${month}/${day}/${year} ${hour.toString().padStart(2, '0')}:${minute} ${ampm}`;
    },

    numberToWords(num) {
      if (isNaN(num) || num === null || num === undefined) return 'Zero';
      return toWords.convert(parseFloat(num));
    },

    handleCancelClick() {
      if (this.invoice.is_gate_pass_in == 1) {
        useToast().error("This invoice cannot be cancelled because it has been gate passed in.");
        return;
      }
      
      const hasCompletedPayment = this.invoice.payments.some(p => p.status == 2 && p.method !== 'Cash');

      if (hasCompletedPayment) {
        useToast().error("This invoice cannot be cancelled because it includes a completed payment.");
        return;
      }

      this.showBottomSheet = true;
    },

    ShowStatus(value) {
      const statusMap = {
        1: 'Pending',
        2: 'Completed',
        3: 'Cancelled',
        4: 'Redeemed'
      };
      return statusMap[value] || 'Unknown';
    },

    //Mask bank account numbers as ****XXXX
    maskBankAccount(accountNo) {
      if (!accountNo) return '—';
      const account = accountNo.toString();
      if (account.length <= 4) return account;
      const lastFour = account.slice(-4);
      const masked = '*'.repeat(account.length - 4);
      return masked + lastFour;
    },

    //Format number to currency
    formatNumber(value) {
      if (!value) return '0.00';
      return parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    //Close Invoice Popup Modal
    closeModal() {
      this.$emit('close');
    },

    //Print the invoice — Times New Roman 12pt, same Tax Invoice layout as screen
    //Print the invoice — Times New Roman 12pt, same Tax Invoice layout as screen
    printInvoice() {
      const printWindow = window.open('', '_blank');

      const isVat = this.isVatInvoice;
      const isCustomer = this.invoice.is_customer_invoice;

      // Purchaser block
      const tinRow = isVat
        ? `<div class="inv-field"><span class="inv-label-col">Purchaser's TIN :</span><span class="inv-value">${isCustomer ? (this.invoice.customer_vat_no || 'N/A') : (this.invoice.dealer_vat_no || 'N/A')}</span></div>`
        : '';

      const purchaserName  = this.purchaserName;
      const purchaserAddr  = this.purchaserAddr;
      const purchaserTel   = this.purchaserTel;

      // Item rows
      const itemRows = this.invoice.items.map(item => `
        <tr>
          <td class="text-left">${item.item_sap_code || '—'}</td>
          <td class="text-left">${item.item_name} (${String(item.invoice_type || '').toUpperCase()})</td>
          <td class="text-center">${parseInt(item.item_qty)}</td>
          <td class="text-right">${this.formatNumber(item.item_price)}</td>
          <td class="text-right">${this.formatNumber(item.item_net_total)}</td>
        </tr>
      `).join('');

      const vatFooterRow = isVat
        ? `<tr class="summary-tr"><td colspan="4">VAT Amount (Total Value of Supply ${this.invoice.vat_percentage}%)</td><td class="text-right">Rs. ${this.invoice.vat}</td></tr>`
        : '';

      const supplyRows = isVat ? `
        <div class="invoice-row-grid">
          <div class="inv-cell"><span class="inv-label">Date of Supply :</span><span class="inv-value">${this.formatDate(this.invoice.created_at)}</span></div>
          <div class="inv-cell"><span class="inv-label">Place of Supply :</span><span class="inv-value">${this.invoice.distributor_address}</span></div>
        </div>
        <div class="invoice-row-single">
          <div class="inv-cell" style="flex-direction: column; align-items: flex-start; gap: 4px;">
            <div class="inv-label">Additional Information if any: </div>
            ${!isCustomer ? `
              <div class="inv-value" style="margin-left: 0;">Name: ${this.invoice.dealer_name || ''}</div>
              <div class="inv-value" style="margin-left: 0;">Address: ${this.invoice.dealer_address || ''}</div>
              <div class="inv-value" style="margin-left: 0;">Telephone: ${this.invoice.dealer_mobile || ''}</div>
            ` : ''}
          </div>
        </div>
      ` : '';

      const invoiceNoLabel = isVat ? 'Tax Invoice No. :' : 'Invoice Number :';
      const invoiceNoValue = this.invoice.invoice_no;

      const amountInWords = this.numberToWords(parseFloat(String(this.invoice.gross_total).replace(/,/g, '')));

      const supplierTinRow = this.invoice.distributor_vat_liable == 1
        ? `<div class="inv-field"><span class="inv-label-col">Supplier's TIN :</span><span class="inv-value">${this.invoice.distributor_vat_no || 'N/A'}</span></div>`
        : '';

      // Payment History Rows (Status and Cancellation Details excluded for printed PDF)
      const paymentRows = (this.invoice.payments || []).map(p => {
        let detailsHtml = '';
        const method = p.method || 'Unknown';
        
        if (method === 'Cheque') {
          const no = p.cheque_no || '—';
          const date = p.cheque_date && p.cheque_date !== '1990-01-01' ? this.formatTimeStamp(p.cheque_date) : '—';
          detailsHtml = `<div class="payment-detail">No: ${no}, Date: ${date}</div>`;
        } else if (method === 'Bank Transfer') {
          const details = p.bank_account_details || {};
          const acc = details.account_no ? this.maskBankAccount(details.account_no) : '—';
          const bank = details.bank_name || '—';
          const branch = details.branch_name || '—';
          detailsHtml = `<div class="payment-detail">Acc/No: ${acc}, Bank: ${bank}, Branch: ${branch}</div>`;
        } else if (method === 'Advance') {
          const adv = p.advance_payment || {};
          const origMethod = adv.pay_method || '—';
          let extra = '';
          if (origMethod === 'Cheque') {
            const no = adv.cheque_no || '—';
            const date = adv.cheque_date && adv.cheque_date !== '1990-01-01' ? this.formatTimeStamp(adv.cheque_date) : '—';
            extra = ` (No: ${no}, Date: ${date})`;
          } else if (origMethod === 'Bank Transfer' || adv.bank_reference) {
            extra = ` (Ref: ${adv.bank_reference || '—'})`;
          } else if (origMethod === 'Credit/Debit Card') {
            const cType = adv.card_type || '—';
            const last4 = adv.card_last4_digits || '—';
            extra = ` (Card Type: ${cType}, Last 4 digits: ${last4})`;
          } else if (origMethod === 'Mobile Payment') {
            const mType = adv.mobile_payment_type || '—';
            const mProv = adv.mobile_provider || '—';
            extra = ` (Type: ${mType}, Provider: ${mProv})`;
          } else if (origMethod === 'Online Payment') {
            extra = ` (Gateway: ${adv.online_gateway || '—'})`;
          }
          detailsHtml = `<div class="payment-detail">Original Method: ${origMethod}${extra}</div>`;
        } else if (method === 'Credit/Debit Card') {
          const cType = p.card_type || '—';
          const last4 = p.card_last4_digits || '—';
          detailsHtml = `<div class="payment-detail">Card Type: ${cType}, Last 4 digits: ${last4}</div>`;
        } else if (method === 'Mobile Payment') {
          const mType = p.mobile_payment_type || '—';
          const mProv = p.mobile_provider || '—';
          detailsHtml = `<div class="payment-detail">mobile_payment_type: ${mType}, mobile_provider: ${mProv}</div>`;
        } else if (method === 'Online Payment') {
          detailsHtml = `<div class="payment-detail">online_gateway: ${p.online_gateway || '—'}</div>`;
        }

        return `
          <tr>
            <td>
              <div>${method}</div>
              ${detailsHtml}
            </td>
            <td class="text-right">${this.formatNumber(p.amount)}</td>
            <td class="text-center">${p.paid_at ? this.formatTimeStamp(p.paid_at) : '—'}</td>
          </tr>
        `;
      }).join('');
      
      const paymentHistoryTable = this.showHistory && this.invoice.payments && this.invoice.payments.length > 0 ? `
        <div style="margin-top: 24pt; page-break-inside: avoid;">
          <h3 style="font-size: 14pt; margin-bottom: 8pt; font-family: 'Times New Roman', Times, serif; font-weight: normal;">Payment History</h3>
          <table class="invoice-table" style="border-top: 0.25px solid black;">
            <thead>
              <tr>
                <th class="text-left">Payment Method</th>
                <th class="text-right">Amount (Rs)</th>
                <th class="text-center">Timestamp</th>
              </tr>
            </thead>
            <tbody>
              ${paymentRows}
            </tbody>
          </table>
        </div>
      ` : '';

      printWindow.document.write(`
        <html>
          <head>
            <title>${this.invoiceTitle}</title>
            <style>
              * { box-sizing: border-box; margin: 0; padding: 0; }
              @page {
                margin: 0.5in;
                @bottom-right {
                  content: "Page " counter(page) " of " counter(pages);
                  font-family: 'Times New Roman', Times, serif;
                  font-size: 10pt;
                }
              }
              body {
                font-family: 'Times New Roman', Times, serif;
                font-size: 12pt;
                color: #000;
              }
              .invoice-document {
                width: 100%;
              }
              /* Title */
              .invoice-title-box {
                text-align: center;
                margin-bottom: 18pt;
              }
              .invoice-title-box h2 {
                display: inline-block;
                border: 0.25px solid black;
                padding: 6pt 24pt;
                font-size: 16pt;
                font-family: 'Times New Roman', Times, serif;
                letter-spacing: 2px;
              }
              /* Two-column row */
              .invoice-row-grid {
                display: grid;
                grid-template-columns: 1fr 1fr;
                column-gap: 15px;
                margin-bottom: 12px;
              }
              /* Single full-width row */
              .invoice-row-single {
                margin-bottom: 12px;
              }
              .inv-cell {
                border: 0.25px solid black;
                padding: 6pt 8pt;
                min-height: 24pt;
                height: auto;
                display: flex;
                align-items: baseline;
                gap: 4px;
                flex-wrap: wrap;
                overflow: visible;
              }
              .inv-cell-tall {
                min-height: 90pt;
                height: auto;
                display: flex;
                flex-direction: column;
                align-items: stretch;
              }
              .inv-field {
                display: flex;
                flex-wrap: nowrap;
                align-items: baseline;
                gap: 0;
                margin-bottom: 4pt;
              }
              .inv-field-bottom {
                margin-top: auto;
              }
              .inv-label {
                font-weight: normal;
                white-space: nowrap;
              }
              .inv-label-col {
                display: inline-block;
                min-width: 110pt;
                font-weight: normal;
                white-space: nowrap;
                flex-shrink: 0;
              }
              .inv-value {
                font-weight: normal;
                word-break: break-word;
                white-space: normal;
              }

              /* Items table */
              .invoice-table {
                width: 100%;
                border-collapse: collapse;
                margin-top: 0;
                margin-bottom: 12px;
                border: 0.25px solid black;
              }
              .invoice-table th,
              .invoice-table td {
                border: 0.25px solid black;
                padding: 5pt 7pt;
                text-align: left;
                font-family: 'Times New Roman', Times, serif;
                font-size: 12pt;
              }
              .invoice-table th {
                text-align: center;
                font-weight: normal;
              }
              .col-ref  { width: 13%; }
              .col-desc { width: 37%; }
              .col-qty  { width: 12%; }
              .col-unit { width: 14%; }
              .col-amt  { width: 24%; }
              .text-left   { text-align: left !important; }
              .text-center { text-align: center !important; }
              .text-right  { text-align: right !important; }
              .summary-tr td { font-size: 12pt; }
              /* Words / Mode rows reuse inv-cell style */
              .inv-cell-bottom {
                border: 0.25px solid black;
                border-top: none;
                padding: 6pt 8pt;
                display: flex;
                gap: 8pt;
              }
              .payment-detail {
                color: #555;
                font-size: 10pt;
                margin-top: 2pt;
              }
            </style>
          </head>
          <body>
            <div class="invoice-document">

              <!-- Title -->
              <div class="invoice-title-box">
                <h2>${this.invoiceTitle}</h2>
              </div>

              <!-- Row 1: Date | Invoice No -->
              <div class="invoice-row-grid">
                <div class="inv-cell"><span class="inv-label">Date of Invoice:</span><span class="inv-value">${this.formatDate(this.invoice.created_at)}</span></div>
                <div class="inv-cell"><span class="inv-label">${invoiceNoLabel}</span><span class="inv-value">${invoiceNoValue}</span></div>
              </div>

              <!-- Row 2: Supplier | Purchaser -->
              <div class="invoice-row-grid">
                <div class="inv-cell inv-cell-tall">
                  ${supplierTinRow}
                  <div class="inv-field"><span class="inv-label-col">Supplier's Name :</span><span class="inv-value">${this.invoice.distributor_name}</span></div>
                  <div class="inv-field"><span class="inv-label-col">Address :</span><span class="inv-value">${this.invoice.distributor_address}</span></div>
                  <div class="inv-field inv-field-bottom"><span class="inv-label-col">Telephone No.:</span><span class="inv-value">${this.invoice.distributor_mobile}</span></div>
                </div>
                <div class="inv-cell inv-cell-tall">
                  ${tinRow}
                  <div class="inv-field"><span class="inv-label-col">Purchaser's Name :</span><span class="inv-value">${purchaserName}</span></div>
                  <div class="inv-field"><span class="inv-label-col">Address :</span><span class="inv-value">${purchaserAddr}</span></div>
                  <div class="inv-field inv-field-bottom"><span class="inv-label-col">Telephone No.:</span><span class="inv-value">${purchaserTel}</span></div>
                </div>
              </div>

              ${supplyRows}

              <!-- Items Table -->
              <table class="invoice-table">
                <thead>
                  <tr>
                    <th class="col-ref text-left">Reference</th>
                    <th class="col-desc text-left">Description of Goods or Services</th>
                    <th class="col-qty">Quantity</th>
                    <th class="col-unit text-right">Unit Price</th>
                    <th class="col-amt text-right">Amount Excluding VAT (Rs.)</th>
                  </tr>
                </thead>
                <tbody>
                  ${itemRows}
                </tbody>
                <tfoot>
                  <tr class="summary-tr">
                    <td colspan="4">Total Value of Supply:</td>
                    <td class="text-right">Rs. ${this.invoice.net_total}</td>
                  </tr>
                  ${vatFooterRow}
                  <tr class="summary-tr">
                    <td colspan="4">Total Amount/consideration including VAT:</td>
                    <td class="text-right">Rs. ${this.invoice.gross_total}</td>
                  </tr>
                  <tr class="summary-tr">
                    <td colspan="4">Total Paid Amount:</td>
                    <td class="text-right">Rs. ${this.invoice.total_payment}</td>
                  </tr>
                  <tr class="summary-tr">
                    <td colspan="4">Balance Amount:</td>
                    <td class="text-right">Rs. ${this.invoice.balance_payment}</td>
                  </tr>
                </tfoot>
              </table>

              <div class="invoice-row-single" style="margin-top: 12pt;">
                <div class="inv-cell">
                  Total Amount in words:
                  <span>&nbsp;${amountInWords}</span>
                </div>
              </div>

              <div class="invoice-row-single">
                <div class="inv-cell">
                  Mode of Payment:
                  <span>&nbsp;${this.paymentMethods}</span>
                </div>
              </div>

              ${paymentHistoryTable}

              <div class="system-generated-note" style="text-align: center; margin-top: 30pt; font-size: 10pt; font-family: 'Times New Roman', Times, serif;">
                This is a system generated document and signature is not required
              </div>

            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
    },

    //Validate remark field on input change
    validateRemark() {
      if (this.cancelRemark.trim()) {
        this.remarkError = false; // Hide error if user types something
      }
    },

    //Close bottom sheet
    closeBottomSheet() {
      this.showBottomSheet = false;
      this.cancelRemark = ''; // Clear the text
      this.remarkError = false; // Reset the error flag
    },

    //Call cancel remark API
    async confirmCancel() {
      if (!this.cancelRemark.trim()) {
        this.remarkError = true;
        return;
      }

      try {
        this.loading = true;

        const payload = {
          invoice_id: this.invoice.invoice_id,
          cancel_remark: this.cancelRemark,
          profileid: this.profileId
        };

        console.log("Cancel Payload:", payload);

        const response = await cancelInvoice(payload, this.token);
        console.log("API Response:", response.data);

        if (response.data.status) {
          this.toast.success(response.data.message || "Invoice canceled successfully.");
          this.closeBottomSheet();
          this.$emit('close');
        } else {
          this.toast.error(response.data.message || "Failed to cancel invoice.");
        }
      } catch (e) {
        console.error("Cancel Invoice Error:", e.response?.data || e.message);
        this.toast.error(e.response?.data?.message || "An error occurred while cancelling the invoice.");
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
/* =========================================================
   STATUS LABELS — smaller chips
   ========================================================= */
.status-pending {
  background-color: #FFC107;
  color: #FFFFFF;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 400;
  white-space: nowrap;
  display: inline-block;
}
.status-completed {
  background-color: #069E27;
  color: #fff;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 400;
  white-space: nowrap;
  display: inline-block;
}
.status-cancelled {
  background-color: #e53935;
  color: #fff;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 400;
  white-space: nowrap;
  display: inline-block;
}
.status-redeemed {
  background-color: #1B6CB4;
  color: #fff;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 400;
  white-space: nowrap;
  display: inline-block;
}

/* =========================================================
   MODAL — TRUE FULL SCREEN
   ========================================================= */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  z-index: 1000;
  overflow-y: auto;
}

.modal-content {
  background: #fff;
  padding: 48px 30px 30px 30px; /* top padding leaves room for the close btn */
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.15);
  width: 100vw;
  min-height: 100vh;
  position: relative;
}

/* Top-right close icon button */
.modal-close-btn {
  position: absolute;
  top: 12px;
  right: 16px;
  background: none;
  border: 1px solid #ccc;
  border-radius: 6px;
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #555;
  font-size: 16px;
  transition: background 0.2s, color 0.2s;
  z-index: 20;
}
.modal-close-btn:hover {
  background: #f44336;
  border-color: #f44336;
  color: #fff;
}

/* =========================================================
   INVOICE DOCUMENT AREA
   ========================================================= */
.invoice-document {
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
}

/* --- Title box --- */
.invoice-title-box {
  text-align: center;
  margin-bottom: 20px;
}
.invoice-title-box h2 {
  display: inline-block;
  border: 1px solid #000;
  padding: 6px 28px;
  font-size: 20px;
  letter-spacing: 2px;
  color: #000;
}

/* --- Two-column grid row --- */
.invoice-row-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 15px;
  margin-bottom: 12px;
}

/* --- Single full-width row --- */
.invoice-row-single {
  margin-bottom: 12px;
}

/* --- Cell (each bordered box) --- */
.inv-cell {
  border: 1px solid #000;
  padding: 8px 12px;
  min-height: 30px;
  font-size: 14px;
  color: #000;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px;
}
.inv-cell-tall {
  min-height: 110px;
  display: flex;
  flex-direction: column;
  /* override baseline for tall multi-field cells */
  align-items: stretch;
}

.inv-field {
  display: flex;
  gap: 0;
  margin-bottom: 5px;
  flex-wrap: nowrap;
  align-items: baseline;
}
.inv-field-bottom {
  margin-top: auto;
}

/* Standard inline label (for single-field cells) */
.inv-label {
  font-weight: normal;
  white-space: nowrap;
  color: #000;
}
/* Fixed-width column label for multi-field tall cells (Supplier/Purchaser) */
.inv-label-col {
  font-weight: normal;
  white-space: nowrap;
  color: #000;
  min-width: 140px;
  flex-shrink: 0;
}
.inv-value {
  color: #000;
  flex: 1;
}

/* Summary cells: label left, amount right — matches tfoot rows */
.inv-cell-summary {
  justify-content: space-between;
  align-items: baseline;
}
.inv-cell-summary .inv-value {
  text-align: right;
  flex: unset;
  white-space: nowrap;
}

/* =========================================================
   ITEMS TABLE
   ========================================================= */
.invoice-table {
  width: 100%;
  border-collapse: collapse;
  margin: 0 0 12px 0;
  border: 1px solid #000;
}
.invoice-table th,
.invoice-table td {
  border: 1px solid #000;
  padding: 9px 10px;
  text-align: left;
  font-size: 14px;
  color: #000;
}
.invoice-table thead th {
  text-align: center;
  background: #fff;
  font-weight: normal;
}
.col-ref  { width: 13%; }
.col-desc { width: 37%; }
.col-qty  { width: 10%; }
.col-unit { width: 14%; }
.col-amt  { width: 26%; }

.text-left   { text-align: left !important; }
.text-center { text-align: center !important; }
.text-right  { text-align: right !important; }

.item-type {
  color: #555;
  font-size: 12px;
  text-transform: uppercase !important;
}

/* Summary rows in tfoot */
.summary-tr td {
  font-size: 14px;
  background: #fff;
}

/* =========================================================
   PAYMENT HISTORY TABLE — same width as invoice table
   ========================================================= */
.invoice-history {
  margin-top: 24px;
  width: 100%;
  max-width: 1100px;
  margin-left: auto;
  margin-right: auto;
}
.invoice-history h3 {
  font-size: 16px;
  margin-bottom: 12px;
  color: #333;
  font-weight: normal;
}
.invoice-history-table {
  width: 100%;
  border-collapse: collapse;
}
/* Force same width as the invoice document tables */
.invoice-table-fullwidth {
  max-width: 100%;
}
.invoice-history-table th,
.invoice-history-table td {
  border: 1px solid #000;
  padding: 8px 10px;
  text-align: left;
  font-size: 13px;
}
.invoice-history-table th {
  background-color: #f2f2f2;
  font-weight: normal;
}
.payment-detail {
  color: #666;
  font-size: 12px;
  margin-top: 3px;
}
.cancellation-details {
  margin: 0;
  padding: 0;
}
.cancellation-details dd {
  margin: 3px 0;
  font-size: 12px;
}

/* =========================================================
   ACTION BUTTONS — aligned with payment history table edges
   ========================================================= */
.invoice-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  width: 100%;
  max-width: 1100px;
  margin-left: auto;
  margin-right: auto;
  gap: 10px;
}
.btn.button-default {
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  background-color: #007bff;
  color: #ffffff;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.3s, transform 0.3s;
}
.btn.button-default:hover {
  background-color: #0056b3;
  transform: scale(1.05);
}
.btn.button-default:active {
  transform: scale(0.95);
}
.cancel-button {
  padding: 10px 20px;
  font-size: 14px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  background-color: #f44336;
  color: white;
  margin-right: auto;
}
.cancel-button:hover {
  background-color: #d32f2f;
}

/* =========================================================
   BOTTOM SHEET
   ========================================================= */
.bottom-sheet-overlay {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: flex-end;
  z-index: 1100;
}
.bottom-sheet {
  background: #fff;
  width: 100%;
  padding: 20px;
  border-radius: 10px 10px 0 0;
  box-shadow: 0 -4px 10px rgba(0, 0, 0, 0.1);
  position: fixed;
  bottom: 0;
  left: 0;
  max-width: 100%;
}
.bottom-sheet h3 {
  margin-bottom: 20px;
  text-align: left;
}
.bottom-sheet textarea {
  width: 100%;
  height: 100px;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  resize: none;
  margin-bottom: 10px;
}
.bottom-sheet textarea:focus {
  border-color: #1c1f23;
  outline: none;
}
.border-red {
  border-color: red !important;
}
.bottom-sheet-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
.btn-exit {
  background-color: #ccc;
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
}
.btn-cancel {
  background-color: #f44336;
  color: white;
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
}
.btn-exit:hover  { background-color: #999; }
.btn-cancel:hover { background-color: #d32f2f; }
.btn-cancel:disabled { background-color: #ccc; cursor: not-allowed; }

/* =========================================================
   MISC
   ========================================================= */
.error-message {
  color: red;
  font-size: 14px;
  margin-top: -5px;
  margin-bottom: 10px;
}
.character-counter {
  text-align: right;
  font-size: 12px;
  color: #666;
  margin-bottom: 10px;
}
.cancelled-badge {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 150px;
  height: auto;
  z-index: 2000;
}
.mt-4 {
  margin-top: 1.5rem;
}

/* =========================================================
   INVOICE LOADING OVERLAY (inside modal)
   ========================================================= */
.invoice-loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.92);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
}
.invoice-loading-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #555;
  font-size: 15px;
}
.spinner-border-lg {
  width: 3rem;
  height: 3rem;
  border-width: 0.3em;
}

/* Spinner */
.spinner-border {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  vertical-align: text-bottom;
  border: 0.15em solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: spinner-border 0.75s linear infinite;
}
.spinner-border-sm {
  width: 0.875rem;
  height: 0.875rem;
  border-width: 0.1em;
}
@keyframes spinner-border {
  to { transform: rotate(360deg); }
}

/* =========================================================
   RESPONSIVE
   ========================================================= */
@media (max-width: 768px) {
  .invoice-row-grid {
    grid-template-columns: 1fr;
  }
  .modal-content {
    padding: 52px 12px 16px 12px;
  }
  .invoice-table {
    font-size: 12px;
  }
  .invoice-table th,
  .invoice-table td {
    padding: 6px;
  }
  .invoice-history {
    max-width: 100%;
  }
  .inv-label-col {
    min-width: 110px;
  }
}

/* =========================================================
   PRINT — hide action buttons
   ========================================================= */
@media print {
  .invoice-actions,
  .cancel-button,
  .btn.button-default {
    display: none !important;
  }
}
</style>